import type {Dispatch, GetState, ThunkAction} from 'src/reducers';

import * as reduxApi from 'src/utils/redux-api-bff';
import type {
  FilterRule,
  FilterScope,
  UpdateEMSRulePayload,
} from 'src/types/filter';
import {
  TEMP_END_NODE_IDENTIFIER,
  TEMP_NODE_IDENTIFIER,
} from 'src/utils/automation-workflow';
import {
  addTempSplit,
  addTempTransition,
  deleteSplit,
  getAutomationWorkflow,
} from 'src/action-creators/automation-workflow';
import {
  getCombinedId,
  getSplitsPayload,
  getUniqueTempEndNode,
} from 'src/utils/split';
import type {SplitData} from 'src/types/split';


export const ADD_TEMP_SPLIT_NODE = 'split/add-temp';
export const ADD_SPLIT = 'split/add-split';
export const SET_SPLIT_RULES = 'split/set-rules';
export const SET_SPLIT_SCOPE = 'split/set-scope';
export const UPDATE_SPLIT_RULE = 'split/update-rule';
export const UPDATE_SPLIT_SCOPE = 'split/update-scope';
export const REMOVE_SPLIT_RULE = 'split/remove-rule';
export const DELETE_SPLIT = 'split/delete-split';
export const RECEIVE_SPLITS = 'split/receive-splits';

type AddTempSplitNodeAction = {
  type: 'split/add-temp';
  payload: {
    swid: string;
    ifEndNodeId: string;
    elseEndNodeId: string;
    if_display_name: string;
    else_display_name: string;
    t: (arg1: string, arg2: string) => string;
  };
};

type AddSplitAction = {
  type: 'split/add-split';
  payload: {
    swid: string;
    nodeId: string;
    position: number;
    tempEndNodeId: string;
    id: string;
    display_name: string;
  };
};

type SetRulesAction = {
  type: 'split/set-rules';
  payload: {
    swid: string;
    nodeId: string;
    rules: FilterRule[];
  };
};

type SetScopeAction = {
  type: 'split/set-scope';
  payload: {
    swid: string;
    nodeId: string;
    scope: FilterScope | null | undefined;
  };
};

type UpdateRuleAction = {
  type: 'split/update-rule';
  payload: {
    swid: string;
    nodeId: string;
    splitId: string;
    ruleId: string;
    updateObject: UpdateEMSRulePayload;
  };
};

type UpdateScopeAction = {
  type: 'split/update-scope';
  payload: {
    swid: string;
    nodeId: string;
    updateObject: FilterScope | null | undefined;
  };
};

type RemoveRuleAction = {
  type: 'split/remove-rule';
  payload: {
    swid: string;
    nodeId: string;
    ruleHash: string;
  };
};

type DeleteSplitNodeAction = {
  type: 'split/delete-split';
  payload: {
    swid: string;
    nodeId: string;
    position: number;
  };
};

type ReceiveSplitsAction = {
  type: 'split/receive-splits';
  payload: SplitData;
};

export type SplitAction =
  | AddTempSplitNodeAction
  | AddSplitAction
  | SetRulesAction
  | SetScopeAction
  | UpdateRuleAction
  | UpdateScopeAction
  | RemoveRuleAction
  | DeleteSplitNodeAction
  | ReceiveSplitsAction;

//We will be adding temp entries to both automation workflow store and split store
export const addTempSplitNodeAndModifySwimlanes: (args: {
  swid: string;
  tid: string;
  if_display_name: string;
  else_display_name: string;
  t: (key: string, value: string) => string;
}) => ThunkAction<unknown> =
  ({swid, tid, if_display_name, else_display_name, t}) =>
  async (dispatch: Dispatch) => {
    const elseEndNodeId = getUniqueTempEndNode();
    // adding temp split in canvas
    await dispatch(
      addTempSplit({
        swid,
        nodeId: TEMP_NODE_IDENTIFIER,
        tid: elseEndNodeId,
        position: 1,
        display_name: if_display_name,
      }),
    );
    // adding temp split in node
    return dispatch(
      addTempSplitNode(
        swid,
        tid,
        elseEndNodeId,
        if_display_name,
        else_display_name,
        t,
      ),
    );
  };

export const deleteTempSplitAndModifySwimlanes: (
  swid: string,
  nodeId: string,
  position: number,
) => ThunkAction<unknown> =
  (swid, nodeId, position) => async (dispatch: Dispatch) => {
    await dispatch(deleteSplit({swid, nodeId, position}));
    return dispatch(deleteSplitNode(swid, nodeId, position));
  };

export const deleteSplitNode = (
  swid: string,
  nodeId: string,
  position: number,
): DeleteSplitNodeAction => ({
  type: DELETE_SPLIT,
  payload: {swid, nodeId, position},
});

export const addTempSplitNode = (
  swid: string,
  ifEndNodeId: string,
  elseEndNodeId: string,
  if_display_name: string,
  else_display_name: string,
  t: (arg1: string, arg2: string) => string,
): AddTempSplitNodeAction => ({
  type: ADD_TEMP_SPLIT_NODE,
  payload: {
    swid,
    ifEndNodeId,
    elseEndNodeId,
    if_display_name,
    else_display_name,
    t,
  },
});

export const addSplitAndModifySwimlanes: ({
  swid,
  nodeId,
  position,
  id,
  display_name,
}: {
  swid: string;
  nodeId: string;
  position: number;
  id: string;
  display_name: string;
}) => ThunkAction<unknown> =
  ({swid, nodeId, position, id, display_name}) =>
  async (dispatch: Dispatch) => {
    const uniqueTempEndNode = getUniqueTempEndNode();
    //this adds temp split in canvas
    await dispatch(
      addTempSplit({
        swid,
        nodeId,
        tid: uniqueTempEndNode,
        position,
        display_name,
      }),
    );
    //this adds temp split in split store
    return dispatch(
      addSplit({
        swid,
        nodeId,
        position,
        tempEndNodeId: uniqueTempEndNode,
        id,
        display_name,
      }),
    );
  };

export const addSplit = ({
  swid,
  nodeId,
  position,
  id,
  display_name,
  tempEndNodeId,
}: {
  swid: string;
  nodeId: string;
  position: number;
  id: string;
  display_name: string;
  tempEndNodeId: string;
}): AddSplitAction => ({
  type: ADD_SPLIT,
  payload: {swid, nodeId, position, id, display_name, tempEndNodeId},
});

export const setSplitRules = (
  swid: string,
  nodeId: string,
  rules: FilterRule[],
): SetRulesAction => ({
  type: SET_SPLIT_RULES,
  payload: {swid, nodeId, rules},
});

export const setSplitScope = (
  swid: string,
  nodeId: string,
  scope?: FilterScope | null,
): SetScopeAction => ({
  type: SET_SPLIT_SCOPE,
  payload: {swid, nodeId, scope},
});

export const updateSplitRule = (
  swid: string,
  nodeId: string,
  splitId: string,
  ruleId: string,
  updateObject: UpdateEMSRulePayload,
): UpdateRuleAction => ({
  type: UPDATE_SPLIT_RULE,
  payload: {swid, nodeId, splitId, ruleId, updateObject},
});

export const updateSplitScope = (
  swid: string,
  nodeId: string,
  updateObject?: FilterScope | null,
): UpdateScopeAction => ({
  type: UPDATE_SPLIT_SCOPE,
  payload: {swid, nodeId, updateObject},
});

export const removeSplitRule = (
  swid: string,
  nodeId: string,
  ruleHash: string,
): RemoveRuleAction => ({
  type: REMOVE_SPLIT_RULE,
  payload: {swid, nodeId, ruleHash},
});

export const createNewSplitNode: (arg1: {
  from_node_id: string;
  to_node_id: string;
  swimlane_id: string;
  workflow_id: string;
  stateSplitId: string;
  selectedSplitIndexForExistingTree: number | null | undefined;
}) => ThunkAction<unknown> =
  ({
    from_node_id,
    to_node_id,
    swimlane_id,
    workflow_id,
    stateSplitId,
    selectedSplitIndexForExistingTree,
  }) =>
  // @ts-expect-error - TS7030 - Not all code paths return a value.
  async (dispatch: Dispatch) => {
    const payload = {
      // node_type: 'branching',
      swimlane_id: parseInt(swimlane_id, 10),
      workflow_id: parseInt(workflow_id, 10),
      // body: {
      //   branching_node_type: 'CONDITIONAL_SPLIT',
      // },
    } as const;
    const res = await dispatch(
      reduxApi.post('node/split', payload, {
        previous_node: from_node_id,
        next_node: to_node_id,
      }),
    );
    if (res) {
      const createdNodeId = res.composite_id;
      return dispatch(
        saveNewNodeSplits({
          from_node_id: createdNodeId,
          workflow_id,
          stateSplitId,
          swimlane_id,
          selectedSplitIndexForExistingTree,
        }),
      );
    }
  };
export const saveNewNodeSplits: (arg1: {
  from_node_id: string;
  workflow_id: string;
  stateSplitId: string;
  swimlane_id: string;
  selectedSplitIndexForExistingTree: number | null | undefined;
}) => ThunkAction<unknown> =
  ({
    from_node_id,
    workflow_id,
    stateSplitId,
    swimlane_id,
    selectedSplitIndexForExistingTree,
  }) =>
  (dispatch: Dispatch, getState: GetState) => {
    const combinedId = getCombinedId(swimlane_id, stateSplitId);
    const splitArray = getState().split[combinedId];
    const payload = getSplitsPayload(splitArray, from_node_id);
    // in case of creating new split node, the tid of first split is the root node of the existing tree
    // which needs to be moved to another split
    const existingTreeRootNodeId = payload[0].to_node_id;
    if (!existingTreeRootNodeId) {
      throw new Error(
        'Existing tree root node id which needs to be moved to another split not found',
      );
    }
    // make changes to move existing tree to user selected split
    const transitions = payload.map((transition, index) => {
      if (selectedSplitIndexForExistingTree === index) {
        return {...transition, to_node_id: existingTreeRootNodeId};
      } else {
        return {...transition, to_node_id: null};
      }
    });
    return dispatch(reduxApi.post('node/transition/bulk', {transitions})).then(
      () => dispatch(getAutomationWorkflow(workflow_id)),
    );
  };
export const saveSplits: (arg1: {
  from_node_id: string;
  workflow_id: string;
  stateSplitId: string;
  swimlane_id: string;
  hfNodeId: string | null | undefined;
}) => ThunkAction<unknown> =
  ({from_node_id, workflow_id, stateSplitId, swimlane_id, hfNodeId}) =>
  (dispatch: Dispatch, getState: GetState) => {
    const combinedId = getCombinedId(swimlane_id, stateSplitId);
    const splitArray = getState().split[combinedId];
    const payload = getSplitsPayload(splitArray, from_node_id);
    return dispatch(
      reduxApi.post('node/transition/bulk', {transitions: payload}),
    ).then(() => dispatch(getAutomationWorkflow(workflow_id, hfNodeId)));
  };

export const receiveSplits = (payload: SplitData): ReceiveSplitsAction => ({
  type: RECEIVE_SPLITS,
  payload,
});
